import { Heat } from "@/pages/laboratory/chemistry-heat-report/models/Heat";
import { searchHeatNames } from "@/pages/meltshop/repositories/HeatRepository";
import {
	CircularProgress,
	Grid,
	IconButton,
	Paper,
	TextField,
	Typography,
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigatorStyles } from "../styles/useNavigatorStyles";

interface Props {
	value: string | null;
	isExternal?: boolean | null;
	heatStatus?: {
		name: string;
		color: string;
		letter: "S" | "M" | "T" | "O" | "C" | "R" | "F";
	} | null;
	onChange: (value: Heat | null) => void;
}

export const HeatNavigationV3 = ({
	value,
	onChange,
	isExternal,
	heatStatus = null,
}: Props) => {
	const classes = useNavigatorStyles();
	const isFirstRender = useRef(true);
	const [heats, setHeats] = useState<Heat[]>([]);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [isFocused, setFocused] = useState(false);
	const [selectedHeat, setSelectedHeat] = useState<Heat | null>(null);

	const currentSearch = useRef(searchTerm);
	currentSearch.current = searchTerm;

	const debouncedSearch = useCallback(
		debounce(async (term: string) => {
			setLoading(true);
			try {
				if (term.length >= 2) {
					const resp = await searchHeatNames({
						searchText: term,
						IncludeIsExternal: isExternal === undefined ? null : isExternal,
					});
					if (resp.ok) {
						const rows = resp.data.tables[0].rows;
						if (currentSearch.current === term) {
							setHeats(rows);
							if (!isFocused)
								setSelectedHeat(
									rows.find((h: Heat) => h.HeatName === value) || null,
								);
						}
					} else {
						setError(resp.message);
					}
				}
				if (term.length === 0) {
					const resp = await searchHeatNames({
						searchText: "",
						IncludeIsExternal: isExternal === undefined ? null : isExternal,
					});
					if (resp.ok) {
						if (currentSearch.current === term) {
							setHeats(resp.data.tables[0].rows);
						}
					} else {
						setError(resp.message);
					}
				}
			} catch (error) {
				setError("An error occurred while searching");
				setHeats([]);
			} finally {
				setLoading(false);
			}
		}, 300),
		[value, isExternal, isFocused],
	);

	useEffect(() => {
		debouncedSearch(searchTerm);
		return () => {
			debouncedSearch.cancel();
		};
	}, [searchTerm, debouncedSearch]);

	const options = useMemo(() => {
		if (selectedHeat && !heats.some((h) => h.HeatId === selectedHeat.HeatId)) {
			return [selectedHeat, ...heats];
		}
		return heats;
	}, [heats, selectedHeat]);

	useEffect(() => {
		if (!isFocused && !loading) {
			if (
				(!value || value === "") &&
				isFirstRender.current &&
				heats.length > 0
			) {
				const defaultHeat = heats[0];
				setSelectedHeat(defaultHeat);
				setSearchTerm(defaultHeat.HeatName);
				onChange(defaultHeat);
				isFirstRender.current = false;
			}
			if (value && searchTerm !== value) {
				const heatFromValue = heats.find((h) => h.HeatName === value) || null;
				setSelectedHeat(heatFromValue);
				setSearchTerm(value);
			}
		}
	}, [isFocused, loading, value, heats, searchTerm, onChange]);

	const handleSelectionChange = (event: any, newValue: Heat | null) => {
		if (newValue) {
			if (!selectedHeat || newValue.HeatId !== selectedHeat.HeatId) {
				setSelectedHeat(newValue);
				onChange(newValue);
			}
		} else {
			setSelectedHeat(null);
		}
	};

	return (
		<Paper className={classes.paper}>
			<Grid container item xs={12} md={12} className={classes.container}>
				<IconButton
					onClick={() => {
						if (selectedHeat?.PreviousHeatId) {
							const previousHeat = {
								...selectedHeat,
								HeatId: selectedHeat.PreviousHeatId,
								HeatName: selectedHeat.PreviousHeatName!,
							};
							onChange(previousHeat);
						}
					}}
					disabled={!selectedHeat?.PreviousHeatName}
					className={classes.iconButton}
				>
					<ChevronLeft style={{ cursor: "pointer" }} />
				</IconButton>
				<Autocomplete<Heat>
					autoHighlight
					blurOnSelect
					clearOnEscape
					selectOnFocus
					options={options}
					getOptionLabel={(option) => option.HeatName}
					value={selectedHeat}
					inputValue={searchTerm}
					onInputChange={(event, newInputValue) => {
						setSearchTerm(newInputValue);
					}}
					onChange={handleSelectionChange}
					onFocus={() => {
						setFocused(true);
					}}
					onBlur={() => {
						setFocused(false);
					}}
					loading={loading}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="outlined"
							label="Heat"
							size="small"
							error={!!error}
							helperText={error}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loading ? (
											<CircularProgress color="inherit" size={20} />
										) : (
											params.InputProps.endAdornment
										)}
									</>
								),
							}}
						/>
					)}
					renderOption={(option) => (
						<Typography
							style={{ color: option.IsExternal ? "blue" : "black" }}
							noWrap
						>
							{option.HeatName}
						</Typography>
					)}
					style={{ width: 200 }}
				/>
				<IconButton
					onClick={() => {
						if (selectedHeat?.NextHeatId) {
							const nextHeat = {
								...selectedHeat,
								HeatId: selectedHeat.NextHeatId,
								HeatName: selectedHeat.NextHeatName!,
							};
							onChange(nextHeat);
						}
					}}
					disabled={!selectedHeat?.NextHeatName}
					className={classes.iconButton}
				>
					<ChevronRight style={{ cursor: "pointer" }} />
				</IconButton>
				{heatStatus && (
					<div
						className={classes.statusContainer}
						style={{ backgroundColor: heatStatus.color ?? "grey" }}
					>
						<Typography variant="body2">{heatStatus.name}</Typography>
					</div>
				)}
			</Grid>
		</Paper>
	);
};
